/**
 * D20: the floor manager's clock. A run of show is segments with minutes on
 * them; once the clock starts, this says which segment the show should be
 * in, how far along it is, and what to tell the host — in words a floor
 * manager would use. Pure, so it is tested without a clock.
 */
export interface PlanSegment { title: string; minutes: number; note?: string }
export interface ClockState {
  /** Index of the segment the show should be in now; the last one once the plan is spent. */
  index: number;
  /** Seconds into the whole show, and into this segment. */
  elapsed: number; into: number;
  /** This segment's planned seconds, and how far past the whole plan the show is (0 while inside it). */
  planned: number; over: number;
  /** What the floor manager says. */
  nudge: string;
  /** 0–1 through this segment (past 1 once over). */
  progress: number;
  done: boolean;
}

export function mmss(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export function showClock(plan: PlanSegment[], startedAt: string | null | undefined, now: Date = new Date()): ClockState | null {
  if (!plan.length || !startedAt) return null;
  const t0 = new Date(startedAt).getTime();
  if (Number.isNaN(t0)) return null;
  const elapsed = Math.max(0, (now.getTime() - t0) / 1000);
  let acc = 0;
  for (let i = 0; i < plan.length; i++) {
    const planned = Math.max(1, plan[i]!.minutes) * 60;
    if (elapsed < acc + planned || i === plan.length - 1) {
      const into = elapsed - acc;
      const left = planned - into;
      const last = i === plan.length - 1;
      const over = last ? Math.max(0, elapsed - (acc + planned)) : 0;
      const next = plan[i + 1]?.title;
      const nudge = over > 0 ? `${mmss(over)} over the plan — wrap up`
        : left <= 30 && !last ? `30 seconds — then ${next}`
        : left <= 120 && !last ? `${Math.ceil(left / 60)} min left in this segment — ${next} is next`
        : last && left <= 120 ? `${Math.ceil(left / 60)} min left — the call to action`
        : `${mmss(left)} left in "${plan[i]!.title}"`;
      return { index: i, elapsed, into, planned, over, nudge, progress: into / planned, done: over > 0 };
    }
    acc += planned;
  }
  return null;
}

export function planTotal(plan: PlanSegment[]): number {
  return plan.reduce((a, s) => a + Math.max(0, s.minutes), 0);
}
